/**
 * FoodPulse — Terms of Service page.
 *
 * Renders all 13 chapters with:
 *   • A sticky sidebar TOC on desktop (`data-testid="fp-terms-toc"`)
 *   • A collapsible chapter picker on mobile
 *   • Bookmarked chapter anchors (`#chapter-1` … `#chapter-13`) so users
 *     can deep-link to any chapter and copy chapter permalinks.
 *
 * Route: /food-pulse/legal/terms
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUp, ChevronDown, FileText, Link2, ScrollText } from "lucide-react";
import { FOODPULSE_TERMS, FOODPULSE_TERMS_META } from "@/data/foodpulseTerms";
import { toast } from "sonner";

function Bullets({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-3 space-y-1.5 text-[15px] leading-relaxed pl-5 list-disc" style={{ color: "#3E2A0A" }}>
      {items.map((b, i) => <li key={i}>{b}</li>)}
    </ul>
  );
}

function Section({ s }) {
  return (
    <section className="mt-8" data-testid={`fp-terms-section-${s.n}`}>
      <h3 className="font-display text-xl md:text-2xl leading-snug" style={{ color: "#1A1613" }}>
        <span className="opacity-40 mr-3 font-mono text-base">{s.n}.</span>
        {s.title}
      </h3>
      {s.body && (
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#3E2A0A" }}>{s.body}</p>
      )}
      <Bullets items={s.bullets} />
      {s.after_bullets && (
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#3E2A0A" }}>{s.after_bullets}</p>
      )}
      <Bullets items={s.bullets_2} />
      {s.subgroups?.map((g, i) => (
        <div key={i} className="mt-5 pl-3 border-l-2" style={{ borderColor: "#E9D8B4" }}>
          <h4 className="font-display text-lg" style={{ color: "#3E2A0A" }}>{g.subtitle}</h4>
          {g.body && <p className="mt-2 text-[15px] leading-relaxed" style={{ color: "#3E2A0A" }}>{g.body}</p>}
          <Bullets items={g.bullets} />
          {g.footer_note && (
            <p className="mt-2 text-sm italic" style={{ color: "#6b5a3a" }}>{g.footer_note}</p>
          )}
        </div>
      ))}
      {s.footer_note && (
        <p className="mt-4 text-sm italic" style={{ color: "#6b5a3a" }}>{s.footer_note}</p>
      )}
    </section>
  );
}

function ChapterBlock({ chapter }) {
  const anchor = `chapter-${chapter.n}`;
  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success(`Copied link to Chapter ${chapter.n}`);
    } catch {
      toast.error("Couldn't copy — you can still bookmark this URL from the address bar.");
    }
  };
  return (
    <article
      id={anchor}
      data-testid={`fp-terms-chapter-${chapter.n}`}
      className="scroll-mt-24 mt-16 first:mt-0 pt-4 border-t"
      style={{ borderColor: "#E9D8B4" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#B25D38" }}>
            Chapter {chapter.n}
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: "#1A1613" }}>
            {chapter.title}
          </h2>
          {chapter.blurb && (
            <p className="mt-2 text-sm md:text-base opacity-80 max-w-3xl" style={{ color: "#3E2A0A" }}>
              {chapter.blurb}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={copyLink}
          className="mt-2 shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest opacity-70 hover:opacity-100 hover:underline"
          data-testid={`fp-terms-copylink-${chapter.n}`}
          title="Copy link to this chapter"
        >
          <Link2 className="w-3.5 h-3.5" /> Copy link
        </button>
      </div>

      {chapter.sections.map((s) => <Section key={s.n} s={s} />)}
    </article>
  );
}

export default function FoodPulseTerms() {
  const { hash } = useLocation();
  const [activeChapter, setActiveChapter] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const chapters = FOODPULSE_TERMS;
  const canonical = useMemo(
    () => `${typeof window !== "undefined" ? window.location.origin : "https://foodpulse.net"}/food-pulse/legal/terms`,
    [],
  );

  // Scroll to hash on load (React Router doesn't do this automatically).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [hash]);

  // Track which chapter is currently in view for the sticky TOC highlight.
  useEffect(() => {
    const els = chapters
      .map((c) => document.getElementById(`chapter-${c.n}`))
      .filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const n = Number(visible.target.id.replace("chapter-", ""));
          if (n) setActiveChapter(n);
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.1, 0.5, 1] },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [chapters]);

  // "Back to top" button once user has scrolled past the hero.
  useEffect(() => {
    const on = () => setShowTop(window.scrollY > 600);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-terms">
      <Helmet>
        <title>FoodPulse — Terms of Service</title>
        <meta name="description" content="FoodPulse Terms of Service — 13 chapters covering marketplace, food rescue, AI, privacy, IP, and dispute resolution." />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="fp-terms-hero">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-4" style={{ color: "#8B5E10" }}>
            — Food Pulse · legal
          </div>
          <h1 className="font-display leading-[1.05] tracking-tight text-[44px] sm:text-[64px] lg:text-[76px] mb-3" style={{ color: "#1A1613" }}>
            Terms of <em style={{ color: "#B25D38", fontStyle: "italic" }}>Service.</em>
          </h1>
          <p className="text-sm sm:text-base max-w-2xl mb-4" style={{ color: "#4B3A20" }}>
            {FOODPULSE_TERMS_META.intro}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }}>
            <span data-testid="fp-terms-effective">Effective · {FOODPULSE_TERMS_META.effective}</span>
            <span className="opacity-40">/</span>
            <span data-testid="fp-terms-updated">Updated · {FOODPULSE_TERMS_META.updated}</span>
            <span className="opacity-40">/</span>
            <span>Version 1.0</span>
          </div>
        </div>
      </section>

      {/* Mobile chapter picker */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b" style={{ borderColor: "#E9D8B4" }}>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="fp-terms-mobile-toc-toggle"
          className="w-full max-w-6xl mx-auto flex items-center justify-between px-4 py-3 text-sm"
          style={{ color: "#3E2A0A" }}
        >
          <span className="inline-flex items-center gap-2 font-mono uppercase tracking-wider text-xs">
            <ScrollText className="w-4 h-4" />
            Chapter {activeChapter} of {chapters.length}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileOpen && (
          <ol className="max-w-6xl mx-auto px-4 pb-3 text-sm space-y-1" data-testid="fp-terms-mobile-toc">
            {chapters.map((c) => (
              <li key={c.n}>
                <a
                  href={`#chapter-${c.n}`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`fp-terms-mobile-toc-link-${c.n}`}
                  className={`block px-2 py-1 rounded ${activeChapter === c.n ? "bg-amber-100 font-semibold" : "hover:bg-amber-50"}`}
                  style={{ color: "#3E2A0A" }}
                >
                  <span className="font-mono opacity-60 mr-2">{String(c.n).padStart(2, "0")}</span>
                  {c.title}
                </a>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Body: sticky TOC + chapters */}
      <div className="max-w-6xl mx-auto px-4 py-10 lg:grid lg:grid-cols-[280px_1fr] lg:gap-10">
        {/* Sticky TOC (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-8" data-testid="fp-terms-toc">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-3 inline-flex items-center gap-1.5" style={{ color: "#8B5E10" }}>
              <FileText className="w-3.5 h-3.5" /> Chapters
            </div>
            <ol className="space-y-1 text-sm">
              {chapters.map((c) => {
                const active = activeChapter === c.n;
                return (
                  <li key={c.n}>
                    <a
                      href={`#chapter-${c.n}`}
                      data-testid={`fp-terms-toc-link-${c.n}`}
                      className={`block px-2.5 py-1.5 rounded-lg transition-colors leading-snug ${active ? "text-white" : "hover:bg-white"}`}
                      style={active
                        ? { background: "#8B5E10" }
                        : { color: "#3E2A0A" }}
                    >
                      <span className={`font-mono text-[11px] mr-2 ${active ? "opacity-80" : "opacity-50"}`}>
                        {String(c.n).padStart(2, "0")}
                      </span>
                      {c.title}
                    </a>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6 pt-4 border-t text-[11px] font-mono uppercase tracking-widest opacity-70 space-y-1.5"
                 style={{ borderColor: "#E9D8B4", color: "#3E2A0A" }}>
              <Link to="/policies/foodpulse-privacy-policy" className="block hover:underline"
                    data-testid="fp-terms-related-privacy">
                → Privacy Policy
              </Link>
              <Link to="/policies/foodpulse-cookie-policy" className="block hover:underline"
                    data-testid="fp-terms-related-cookies">
                → Cookie Policy
              </Link>
            </div>
          </div>
        </aside>

        {/* Chapters */}
        <div>
          {chapters.map((c) => <ChapterBlock key={c.n} chapter={c} />)}

          <div className="mt-16 pt-8 border-t text-sm italic" style={{ borderColor: "#E9D8B4", color: "#6b5a3a" }}
               data-testid="fp-terms-end-note">
            End of FoodPulse Terms of Service · Questions? Reach us via the contact methods
            published on <Link to="/food-pulse/about" className="underline" style={{ color: "#8B5E10" }}>the About page</Link>.
          </div>
        </div>
      </div>

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full text-white shadow-lg flex items-center justify-center hover:brightness-110"
          style={{ background: "#8B5E10" }}
          data-testid="fp-terms-back-to-top"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
